import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Download, File, Trash2, Eye } from 'lucide-react';
import { API } from '../utils';

const DocumentsPanel = ({ appointment, canEdit, onDocumentUpdate }) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const documents = appointment.documents || [];

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(t('appointment.messages.maxFileSize')); return; }
    if (documents.length >= 3) { toast.error(t('appointment.messages.maxDocuments')); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/appointments/${appointment.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('appointment.messages.documentUploaded'));
      if (onDocumentUpdate) onDocumentUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('appointment.messages.uploadError'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadDocument = async (doc, view = false) => {
    // Bug fix 2026-07-10: window.open non invia il token -> 401 e il file non si apriva.
    // Ora scarichiamo via axios (autenticato) e apriamo/salviamo il blob.
    try {
      const res = await axios.get(
        `${API}/appointments/${appointment.id}/documents/${doc.id}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: doc.content_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const viewable = /pdf|image/.test(doc.content_type || '');
      if (view && viewable) {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.original_name || 'documento';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Impossibile scaricare il documento');
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm(t('common.confirm') + '?')) return;
    try {
      await axios.delete(`${API}/appointments/${appointment.id}/documents/${docId}`);
      toast.success(t('appointment.messages.documentDeleted'));
      if (onDocumentUpdate) onDocumentUpdate();
    } catch (err) {
      toast.error(t('errors.generic'));
    }
  };

  return (
    <div className="border-t pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-gray-700">{t('appointment.documents.title')}</p>
        {documents.length < 3 && (
          <label className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition cursor-pointer text-sm">
            <Upload size={16} />
            {uploading ? '...' : t('appointment.documents.upload')}
            <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.zip,.rar" />
          </label>
        )}
      </div>

      {documents.length === 0 ? (
        <p className="text-sm text-gray-500 italic">{t('appointment.documents.noDocuments')}</p>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
              <div className="flex items-center gap-2">
                <File size={16} className="text-gray-500" />
                <span className="text-sm truncate max-w-[200px]">{doc.original_name}</span>
                <span className="text-xs text-gray-400">({Math.round(doc.size / 1024)}KB)</span>
              </div>
              <div className="flex items-center gap-1">
                {/pdf|image/.test(doc.content_type || '') && (
                  <button onClick={() => downloadDocument(doc, true)} className="p-1.5 hover:bg-gray-200 rounded transition" title="Visualizza" data-testid={`doc-view-${doc.id}`}>
                    <Eye size={16} className="text-gray-600" />
                  </button>
                )}
                <button onClick={() => downloadDocument(doc)} className="p-1.5 hover:bg-gray-200 rounded transition" title="Scarica" data-testid={`doc-download-${doc.id}`}>
                  <Download size={16} className="text-blue-600" />
                </button>
                {canEdit && (
                  <button onClick={() => deleteDocument(doc.id)} className="p-1.5 hover:bg-gray-200 rounded transition" title="Delete">
                    <Trash2 size={16} className="text-red-600" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-gray-400 mt-2">{t('appointment.documents.uploadHint')}</p>
    </div>
  );
};

export default DocumentsPanel;
